import React, { useState, useEffect } from "react";
import {
  Eye,
  EyeOff,
  Lock,
  ArrowRight,
  CheckCircle,
  XCircle,
  ArrowLeft,
} from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import BaseApi from "../services/base-api";
import { useToast } from "../context/ToastContext";
import { useTranslation } from "react-i18next";
import { APP_NAME } from "../constants/info";
import AppLogo from "../components/AppLogo";

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { success, err } = useToast();

  const token = searchParams.get("token");

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({
    newPassword: "",
    confirmPassword: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isValidToken, setIsValidToken] = useState(true);
  const [isVerifyingToken, setIsVerifyingToken] = useState(true);
  const { i18n, t } = useTranslation();

  // Password strength checker
  const getPasswordStrength = (password: string) => {
    if (!password) return { strength: 0, text: "", color: "" };

    let strength = 0;
    if (password.length >= 8) strength++;
    if (password.length >= 12) strength++;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength++;
    if (/\d/.test(password)) strength++;
    if (/[^a-zA-Z0-9]/.test(password)) strength++;

    if (strength <= 2) return { strength, text: "Weak", color: "bg-red-500" };
    if (strength <= 3)
      return { strength, text: "Medium", color: "bg-yellow-500" };
    return { strength, text: "Strong", color: "bg-green-500" };
  };

  const passwordStrength = getPasswordStrength(newPassword);

  // Verify token on mount
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setIsValidToken(false);
        setIsVerifyingToken(false);
        return;
      }

      try {
        // Simulate API call to verify token
        await new Promise((resolve) => setTimeout(resolve, 1500));

        // In real implementation, call your API to verify token
        // const response = await api.verifyResetToken(token);

        setIsValidToken(true);
      } catch (error) {
        setIsValidToken(false);
      } finally {
        setIsVerifyingToken(false);
      }
    };

    verifyToken();
  }, [token]);

  const validateForm = () => {
    const newErrors = { newPassword: "", confirmPassword: "" };

    if (!newPassword) {
      newErrors.newPassword = t("Password is required");
    } else if (newPassword.length < 8) {
      newErrors.newPassword = t("Password must be at least 8 characters");
    } else if (!/(?=.*[a-z])(?=.*[A-Z])/.test(newPassword)) {
      newErrors.newPassword = t(
        "Password must contain both uppercase and lowercase letters",
      );
    } else if (!/(?=.*\d)/.test(newPassword)) {
      newErrors.newPassword = t("Password must contain at least one number");
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = t("Please confirm your password");
    } else if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = t("Passwords do not match");
    }

    setErrors(newErrors);
    return !newErrors.newPassword && !newErrors.confirmPassword;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (validateForm()) {
      setIsLoading(true);

      try {
        const res = await BaseApi.post("/auth/reset-password", {
          token,
          password: confirmPassword,
        });
        // console.log('Reset Password response res ====> ', res)
        if (res && res?.success) {
          setIsSuccess(true);
          success(t("Your password has been reset successfully"));
          setTimeout(() => {
            navigate("/signin");
          }, 2000);
        } else {
          err(t("Failed to reset your password"));
        }
      } catch (error) {
        setErrors({
          newPassword: "",
          confirmPassword: t("Failed to reset password. Please try again."),
        });
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Loading state while verifying token
  if (isVerifyingToken) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 flex items-center justify-center">
        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
        <div className="text-center">
          <div
            className="w-16 h-16 border-4 border-black border-t-transparent rounded-full mx-auto mb-4"
            style={{ animation: "spin 1s linear infinite" }}
          ></div>
          <p className="text-gray-600 font-medium">
            {t("Verifying reset link...")}
          </p>
        </div>
      </div>
    );
  }

  // Invalid token state
  if (!isValidToken) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="mb-6 flex justify-center">
            <div className="bg-red-100 rounded-full p-4">
              <XCircle className="w-16 h-16 text-red-600" />
            </div>
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            {t("Invalid or Expired Link")}
          </h2>
          <p className="text-gray-600 text-base mb-6">
            {t(
              "This password reset link is invalid or has expired. Please request a new one.",
            )}
          </p>

          <div className="space-y-3">
            <button
              onClick={() => navigate("/forgot-password")}
              className="w-full bg-black text-white py-3 rounded-xl font-semibold hover:bg-gray-700 transition-all duration-300"
            >
              {t("Request New Link")}
            </button>
            <button
              onClick={() => navigate("/signin")}
              className="w-full bg-gray-200 text-gray-800 py-3 rounded-xl font-semibold hover:bg-gray-300 transition-all duration-300"
            >
              {t("Back to Sign In")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 flex items-center justify-center">
      <div className="w-full max-w-6xl mx-auto grid lg:grid-cols-2 gap-0 min-h-screen lg:min-h-[600px]">
        <style>{`
          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(30px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          @keyframes slideInLeft {
            from {
              opacity: 0;
              transform: translateX(-20px);
            }
            to {
              opacity: 1;
              transform: translateX(0);
            }
          }
          @keyframes slideInRight {
            from {
              opacity: 0;
              transform: translateX(20px);
            }
            to {
              opacity: 1;
              transform: translateX(0);
            }
          }
          @keyframes pulse {
            0%, 100% {
              transform: scale(1);
            }
            50% {
              transform: scale(1.05);
            }
          }
          @keyframes spin {
            from {
              transform: rotate(0deg);
            }
            to {
              transform: rotate(360deg);
            }
          }
          @keyframes float {
            0%, 100% {
              transform: translateY(0px);
            }
            50% {
              transform: translateY(-20px);
            }
          }
          @keyframes successFade {
            from {
              opacity: 0;
              transform: scale(0.8);
            }
            to {
              opacity: 1;
              transform: scale(1);
            }
          }
        `}</style>

        {/* Left Side - Branding Section */}
        <div
          className="hidden lg:flex bg-gradient-to-br from-black via-gray-700 to-black p-12 flex-col justify-center items-center relative overflow-hidden"
          style={{
            animation: "slideInLeft 0.8s ease-out",
          }}
        >
          {/* Decorative elements */}
          <div className="absolute top-0 left-0 w-full h-full opacity-10">
            <div
              className="absolute top-10 left-10 w-32 h-32 border-2 border-white rounded-full"
              style={{ animation: "float 6s ease-in-out infinite" }}
            ></div>
            <div
              className="absolute top-40 right-20 w-20 h-20 border-2 border-white rounded-full"
              style={{ animation: "float 8s ease-in-out infinite 1s" }}
            ></div>
            <div
              className="absolute bottom-20 left-20 w-40 h-40 border-2 border-white rounded-full"
              style={{ animation: "float 7s ease-in-out infinite 2s" }}
            ></div>
            <div
              className="absolute bottom-40 right-10 w-24 h-24 border-2 border-white rounded-full"
              style={{ animation: "float 9s ease-in-out infinite 1.5s" }}
            ></div>
          </div>

          <div className="relative z-10 text-center">
            {/* Logo */}
            <div
              className="flex justify-center mb-8"
              style={{
                animation: "pulse 2s ease-in-out infinite",
              }}
            >
              {/* Logo */}
              <AppLogo className="w-16 h-16 bg-white rounded-2xl lg-shadow" />
            </div>

            <h1 className="text-5xl font-bold text-white mb-4">{APP_NAME}</h1>
            <p className="text-gray-300 text-lg mb-8 max-w-md">
              {t(
                "Create a strong password to secure your account and continue shopping.",
              )}
            </p>

            {/* Features */}
            <div className="space-y-4 text-left max-w-md">
              <div className="flex items-start gap-3">
                <div className="bg-white/10 rounded-lg p-2 mt-1">
                  <svg
                    className="w-5 h-5 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                    />
                  </svg>
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-1">
                    {t("Strong Security")}
                  </h3>
                  <p className="text-gray-400 text-sm">
                    {t("Use a mix of letters, numbers, and symbols")}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="bg-white/10 rounded-lg p-2 mt-1">
                  <svg
                    className="w-5 h-5 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-1">
                    {t("Password Requirements")}
                  </h3>
                  <p className="text-gray-400 text-sm">
                    {t("At least 8 characters with uppercase and numbers")}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="bg-white/10 rounded-lg p-2 mt-1">
                  <svg
                    className="w-5 h-5 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-1">
                    {t("Account Protected")}
                  </h3>
                  <p className="text-gray-400 text-sm">
                    {t("Your account will be secured immediately")}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Form Section */}
        <div
          className="flex items-center justify-center p-6 sm:p-8 lg:p-12 bg-white"
          style={{
            animation: "slideInRight 0.8s ease-out",
          }}
        >
          <div className="w-full max-w-md">
            {/* Mobile Logo */}
            <div className="lg:hidden flex justify-center mb-8">
              <div className="cursor-pointer" onClick={() => navigate("/")}>
                <div className="bg-black rounded-2xl p-4 flex items-center justify-center w-16 h-16 shadow-lg">
                  {/* Logo  */}
                  {/* <div className="text-white text-3xl">✦</div> */}
                </div>
              </div>
            </div>

            {!isSuccess ? (
              <>
                {/* Back Button */}
                <button
                  onClick={() => navigate("/signin")}
                  className="flex items-center gap-2 text-gray-600 hover:text-black transition-colors mb-6 group"
                  style={{
                    animation: "slideInRight 0.5s ease-out 0.1s backwards",
                  }}
                >
                  <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
                  <span className="font-medium">Back to Sign In</span>
                </button>

                <div className="mb-8">
                  <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">
                    {t("Reset Password")}
                  </h2>
                  <p className="text-gray-600 text-base sm:text-lg">
                    {t("Enter your new password below")}
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* New Password Field */}
                  <div
                    style={{
                      animation: "slideInRight 0.5s ease-out 0.2s backwards",
                    }}
                  >
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      {t("New Password")}
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <input
                        type={showNewPassword ? "text" : "password"}
                        value={newPassword}
                        onChange={(e) => {
                          setNewPassword(e.target.value);
                          if (errors.newPassword) {
                            setErrors({ ...errors, newPassword: "" });
                          }
                        }}
                        className={`w-full pl-12 pr-14 py-4 rounded-xl border-2 ${
                          errors.newPassword
                            ? "border-red-500 focus:border-red-500"
                            : "border-gray-200 focus:border-black"
                        } focus:outline-none transition-all duration-300 text-base`}
                        placeholder={t("Enter new password")}
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                      >
                        {showNewPassword ? (
                          <EyeOff className="w-5 h-5" />
                        ) : (
                          <Eye className="w-5 h-5" />
                        )}
                      </button>
                    </div>

                    {/* Password Strength Indicator */}
                    {newPassword && (
                      <div className="mt-3">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-medium text-gray-600">
                            {t("Password Strength")}
                          </span>
                          <span
                            className={`text-xs font-semibold ${
                              passwordStrength.strength <= 2
                                ? "text-red-600"
                                : passwordStrength.strength <= 3
                                  ? "text-yellow-600"
                                  : "text-green-600"
                            }`}
                          >
                            {passwordStrength.text}
                          </span>
                        </div>
                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className={`h-full ${passwordStrength.color} transition-all duration-300`}
                            style={{
                              width: `${
                                (passwordStrength.strength / 5) * 100
                              }%`,
                            }}
                          ></div>
                        </div>
                      </div>
                    )}

                    {errors.newPassword && (
                      <p className="mt-2 text-sm text-red-500 flex items-center gap-1">
                        <span className="text-lg">•</span> {errors.newPassword}
                      </p>
                    )}
                  </div>

                  {/* Confirm Password Field */}
                  <div
                    style={{
                      animation: "slideInRight 0.5s ease-out 0.3s backwards",
                    }}
                  >
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      {t("Confirm Password")}
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        value={confirmPassword}
                        onChange={(e) => {
                          setConfirmPassword(e.target.value);
                          if (errors.confirmPassword) {
                            setErrors({ ...errors, confirmPassword: "" });
                          }
                        }}
                        className={`w-full pl-12 pr-14 py-4 rounded-xl border-2 ${
                          errors.confirmPassword
                            ? "border-red-500 focus:border-red-500"
                            : "border-gray-200 focus:border-black"
                        } focus:outline-none transition-all duration-300 text-base`}
                        placeholder={t("Confirm new password")}
                      />
                      <button
                        type="button"
                        onClick={() =>
                          setShowConfirmPassword(!showConfirmPassword)
                        }
                        className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                      >
                        {showConfirmPassword ? (
                          <EyeOff className="w-5 h-5" />
                        ) : (
                          <Eye className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                    {errors.confirmPassword && (
                      <p className="mt-2 text-sm text-red-500 flex items-center gap-1">
                        <span className="text-lg">•</span>{" "}
                        {errors.confirmPassword}
                      </p>
                    )}
                  </div>

                  {/* Password Requirements */}
                  <div
                    className="bg-gray-50 border-2 border-gray-200 rounded-xl p-4"
                    style={{
                      animation: "slideInRight 0.5s ease-out 0.4s backwards",
                    }}
                  >
                    <h4 className="font-semibold text-gray-900 text-sm mb-2">
                      {t("Password must contain")}:
                    </h4>
                    <ul className="space-y-1 text-sm text-gray-600">
                      <li className="flex items-center gap-2">
                        <span
                          className={
                            newPassword.length >= 8
                              ? "text-green-600"
                              : "text-gray-400"
                          }
                        >
                          {newPassword.length >= 8 ? "✓" : "○"}
                        </span>
                        {t("At least 8 characters")}
                      </li>
                      <li className="flex items-center gap-2">
                        <span
                          className={
                            /(?=.*[a-z])(?=.*[A-Z])/.test(newPassword)
                              ? "text-green-600"
                              : "text-gray-400"
                          }
                        >
                          {/(?=.*[a-z])(?=.*[A-Z])/.test(newPassword)
                            ? "✓"
                            : "○"}
                        </span>
                        {t("Uppercase and lowercase letters")}
                      </li>
                      <li className="flex items-center gap-2">
                        <span
                          className={
                            /(?=.*\d)/.test(newPassword)
                              ? "text-green-600"
                              : "text-gray-400"
                          }
                        >
                          {/(?=.*\d)/.test(newPassword) ? "✓" : "○"}
                        </span>
                        {t("At least one number")}
                      </li>
                    </ul>
                  </div>

                  {/* Submit Button */}
                  <div
                    style={{
                      animation: "slideInRight 0.5s ease-out 0.5s backwards",
                    }}
                  >
                    <button
                      type="submit"
                      disabled={isLoading}
                      className={`w-full bg-black text-white py-4 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all duration-300 text-base ${
                        isLoading
                          ? "opacity-70 cursor-not-allowed"
                          : "hover:bg-gray-700 hover:shadow-2xl transform hover:-translate-y-1 active:scale-95"
                      }`}
                    >
                      {isLoading ? (
                        <>
                          <div
                            className="w-5 h-5 border-2 border-white border-t-transparent rounded-full"
                            style={{
                              animation: "spin 1s linear infinite",
                            }}
                          ></div>
                          <span>{t("Resetting Password...")}</span>
                        </>
                      ) : (
                        <>
                          <span>{t("Reset Password")}</span>
                          <ArrowRight className="w-5 h-5" />
                        </>
                      )}
                    </button>
                  </div>
                </form>

                {/* Footer Text */}
                <div
                  className="text-center mt-6"
                  style={{
                    animation: "slideInRight 0.5s ease-out 0.6s backwards",
                  }}
                >
                  <p className="text-sm text-gray-600">
                    {t("Remember your password?")}{" "}
                    <button
                      type="button"
                      onClick={() => navigate("/signin")}
                      className="font-semibold text-black hover:text-gray-700 transition-colors underline decoration-2 underline-offset-2"
                    >
                      Sign In
                    </button>
                  </p>
                </div>
              </>
            ) : (
              /* Success State */
              <div
                className="text-center"
                style={{
                  animation: "successFade 0.6s ease-out",
                }}
              >
                <div className="mb-6 flex justify-center">
                  <div className="bg-green-100 rounded-full p-4">
                    <CheckCircle className="w-16 h-16 text-green-600" />
                  </div>
                </div>

                <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                  {t("Password Reset Successfully")}
                </h2>
                <p className="text-gray-600 text-base sm:text-lg mb-6">
                  {t("password updated")}
                </p>

                <div className="bg-green-50 border-2 border-green-200 rounded-xl p-6 mb-6">
                  <p className="text-green-800 text-sm leading-relaxed">
                    {"security message"}
                  </p>
                </div>

                <button
                  onClick={() => navigate("/signin")}
                  className="w-full bg-black text-white py-4 rounded-xl font-semibold hover:bg-gray-700 transition-all duration-300 flex items-center justify-center gap-2 mb-4"
                >
                  <span>{t("Go to Sign In")}</span>
                  <ArrowRight className="w-5 h-5" />
                </button>

                <p className="text-sm text-gray-500">
                  {t("Redirecting automatically in a moment...")}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
